import {
  DeleteOutlined,
  CopyOutlined,
  HolderOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  LIBRARY_TEMPLATE_ID,
  defineWorksheetTemplateInputSchema,
  isSupportedWorksheetVideo,
} from '@cc/schema';
import { useTranslate, useWarnAboutChange } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  CHOICE_TYPES,
  FIELD_TYPES,
  LANGUAGES,
  NUMERIC_TYPES,
  categoryChoices,
  contentOfDraft,
  learnVideoOptionsOf,
  linkSourceOf,
  moveItem,
  newField,
  newSection,
  numericFields,
  type DraftField,
  type DraftSection,
  type DraftVideo,
  type FieldType,
  type WorksheetDraft,
} from './model';
import type { ImportedFrom } from './importModel';
import { useWorksheetCommand, type WorksheetOperation } from './useWorksheetCommand';
import { WorksheetPhonePreview } from './WorksheetPhonePreview';

/**
 * E34 · "Practice — Worksheets — Builder" (cc-homework, chapter "Worksheets — Templates & library
 * (portal, E34)", screen e34e0009-0001-4000-8000-000000000009) and its "Save as a new version" section —
 * also the backoffice's "Worksheet library — Publish" (the same builder the practices use). OUR OWN
 * Google-Forms-style list editor on antd + dnd-kit (the founder's Q-e34-16 A), rendering the template
 * schema the events carry — no second form schema.
 *
 * Behavior lines implemented here:
 *   · gesture: Save template → DefineWorksheetTemplate (new) · ReviseWorksheetTemplate (existing — "Save
 *     as a new version") · PublishWorksheetTemplate (the backoffice), i18n worksheets.builder.*;
 *   · a vertical list of question cards; click a card → edit it in place: label, type (the eight), required,
 *     options (single / multi choice), min/max (number, scale), i18n worksheets.builder.card.*;
 *   · Duplicate / Delete on each card; Add question and Add section below the list;
 *   · reorder cards and sections by drag OR by keyboard (dnd-kit's keyboard sensor: Space to lift, the
 *     arrows to move, Space to drop), with dnd-kit's screen-reader announcements (i18n
 *     worksheets.builder.reorder.*);
 *   · a side panel with the form's settings — category, language, scoring (sum + bands), signature,
 *     repeatable (i18n worksheets.builder.settings.*);
 *   · a live phone preview of the draft, updated on every edit (`WorksheetPhonePreview`);
 *   · a refusal is shown on the card or setting it names (WORKSHEET_SCORING_FIELD and
 *     WORKSHEET_SIGNED_JOURNAL on the settings; WORKSHEET_TEMPLATE_EMPTY and the rest above the form);
 *   · Add video in a section → the video card: a CopingCard Learn video (the catalogue of the template's
 *     language) or a YouTube / Vimeo link ("CopingCard stores no video"), its title, Remove; one video per
 *     section; a link that is not YouTube or Vimeo is refused on the card before Save (the shared Yup
 *     check);
 *   · a copy of a library template keeps its video until the practice replaces it.
 *   · block 5b — "Import from a file" (a header button, `headerExtra`) opens the import section; an imported
 *     draft opens HERE, unsaved, with its banner (`notice`), and Save records DefineWorksheetTemplate with
 *     `importedFrom {format, pages}` — the provenance only, never the file (Q-e34-17 A).
 *   · Q-ugc-2 A — the Builder never asks for "I have the right to use this content" (define · revise ·
 *     library copy · publish): only Import from a file does. An imported draft carries the tick given at the
 *     import (`rightsConfirmed`) through to its Save; nothing else is sent.
 */
type Props = {
  mode: 'define' | 'revise' | 'publish';
  initial: WorksheetDraft;
  /** The template a revise / a library re-publish saves; absent for a new one. */
  templateId?: string | null;
  onSaved: (saved: { templateId: string; version: number | null }) => void;
  onCancel: () => void;
  heading: ReactNode;
  /** block 5b — a draft from an import: Save names where it came from (Define only). */
  importedFrom?: ImportedFrom | null;
  /** block 5b — shown above the editor (the imported draft's banner and notes). */
  notice?: ReactNode;
  /** block 5b — the header's extra action (Import from a file) and the Cancel button's own words. */
  headerExtra?: ReactNode;
  cancelLabel?: string;
  /** Q-ugc-2 A — the import's tick, carried to an imported draft's Save; the Builder itself never asks. */
  rightsConfirmed?: boolean;
};

const OPERATION: Record<Props['mode'], WorksheetOperation> = {
  define: 'defineWorksheetTemplate',
  revise: 'reviseWorksheetTemplate',
  publish: 'publishWorksheetTemplate',
};

/** The refusals the settings panel names (the Behavior: "shown on the card or setting it names"). */
const SETTINGS_CODES = ['WORKSHEET_SCORING_FIELD', 'WORKSHEET_SIGNED_JOURNAL'];

const Sortable = ({
  id,
  children,
}: {
  id: string;
  children: (handle: ReactNode) => ReactNode;
}) => {
  const t = useTranslate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const handle = (
    <Tooltip title={t('worksheets.builder.reorder.handle')}>
      <Button
        type="text"
        size="small"
        icon={<HolderOutlined />}
        aria-label={t('worksheets.builder.reorder.handle')}
        data-testid={`worksheet-handle-${id}`}
        {...attributes}
        {...listeners}
      />
    </Tooltip>
  );
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      {children(handle)}
    </div>
  );
};

export const WorksheetBuilder = ({
  mode,
  initial,
  templateId,
  onSaved,
  onCancel,
  heading,
  importedFrom = null,
  notice = null,
  headerExtra = null,
  cancelLabel,
  rightsConfirmed = false,
}: Props) => {
  const t = useTranslate();
  const { message } = AntdApp.useApp();
  const { run } = useWorksheetCommand();
  const [draft, setDraft] = useState<WorksheetDraft>(initial);
  const [libraryId, setLibraryId] = useState(templateId ?? '');
  const [activeField, setActiveField] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [settingsProblem, setSettingsProblem] = useState<string | null>(null);
  const [showVideoErrors, setShowVideoErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  // NO AUTOSAVE (portal conventions): an edit only marks the draft unsaved — leaving warns; Save writes
  const { setWarnWhen } = useWarnAboutChange();
  const initialJson = useMemo(() => JSON.stringify(initial), [initial]);
  useEffect(() => {
    setWarnWhen(JSON.stringify(draft) !== initialJson);
  }, [draft, initialJson, setWarnWhen]);
  useEffect(() => () => setWarnWhen(false), [setWarnWhen]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /** What a screen reader hears while a card or a section moves (dnd-kit's live region). */
  const announcements: Announcements = useMemo(
    () => ({
      onDragStart: ({ active }) =>
        t('worksheets.builder.reorder.announce.start', { id: String(active.id) }),
      onDragOver: ({ active, over }) =>
        over
          ? t('worksheets.builder.reorder.announce.over', {
              id: String(active.id),
              over: String(over.id),
            })
          : t('worksheets.builder.reorder.announce.outside', { id: String(active.id) }),
      onDragEnd: ({ active, over }) =>
        over
          ? t('worksheets.builder.reorder.announce.end', {
              id: String(active.id),
              over: String(over.id),
            })
          : t('worksheets.builder.reorder.announce.cancel', { id: String(active.id) }),
      onDragCancel: ({ active }) =>
        t('worksheets.builder.reorder.announce.cancel', { id: String(active.id) }),
    }),
    [t],
  );
  const accessibility = {
    announcements,
    screenReaderInstructions: {
      draggable: t('worksheets.builder.reorder.instructions'),
    },
  };

  const patch = (next: Partial<WorksheetDraft>) => setDraft((d) => ({ ...d, ...next }));
  const patchSection = (sid: string, next: Partial<DraftSection>) =>
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s) => (s.sectionId === sid ? { ...s, ...next } : s)),
    }));
  const patchField = (sid: string, fid: string, next: Partial<DraftField>) =>
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s) =>
        s.sectionId !== sid
          ? s
          : { ...s, fields: s.fields.map((f) => (f.fieldId === fid ? { ...f, ...next } : f)) },
      ),
    }));

  // a new card's id comes from the draft on screen (the next free `f<n>`); the new card opens for editing
  const addField = (sid: string, type: FieldType = 'longText') => {
    const f = newField(type, draft);
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s) =>
        s.sectionId === sid ? { ...s, fields: [...s.fields, f] } : s,
      ),
    }));
    setActiveField(f.fieldId);
  };
  const duplicateField = (sid: string, fid: string) => {
    const s = draft.sections.find((x) => x.sectionId === sid);
    const at = s?.fields.findIndex((f) => f.fieldId === fid) ?? -1;
    if (!s || at < 0) return;
    const copy = { ...s.fields[at], fieldId: newField('text', draft).fieldId };
    const fields = [...s.fields];
    fields.splice(at + 1, 0, copy);
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((x) => (x.sectionId === sid ? { ...x, fields } : x)),
    }));
    setActiveField(copy.fieldId);
  };
  const deleteField = (sid: string, fid: string) =>
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s) =>
        s.sectionId === sid ? { ...s, fields: s.fields.filter((f) => f.fieldId !== fid) } : s,
      ),
      scoring: { ...d.scoring, fields: d.scoring.fields.filter((x) => x !== fid) },
    }));
  const addSection = () =>
    setDraft((d) => ({ ...d, sections: [...d.sections, newSection(d)] }));
  const deleteSection = (sid: string) =>
    setDraft((d) => {
      const gone = d.sections.find((s) => s.sectionId === sid)?.fields.map((f) => f.fieldId) ?? [];
      return {
        ...d,
        sections: d.sections.filter((s) => s.sectionId !== sid),
        scoring: { ...d.scoring, fields: d.scoring.fields.filter((x) => !gone.includes(x)) },
      };
    });

  const onSectionsDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setDraft((d) => {
      const ids = d.sections.map((s) => s.sectionId);
      return {
        ...d,
        sections: moveItem(d.sections, ids.indexOf(String(active.id)), ids.indexOf(String(over.id))),
      };
    });
  };
  const onFieldsDragEnd = (sid: string) => ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s) => {
        if (s.sectionId !== sid) return s;
        const ids = s.fields.map((f) => f.fieldId);
        return {
          ...s,
          fields: moveItem(s.fields, ids.indexOf(String(active.id)), ids.indexOf(String(over.id))),
        };
      }),
    }));
  };

  const videoProblem = (v: DraftVideo | null): boolean =>
    !!v && (!v.ref.trim() || !isSupportedWorksheetVideo(v.source, v.ref.trim()));

  const save = async () => {
    setProblem(null);
    setSettingsProblem(null);
    if (!draft.name.trim()) {
      setProblem(t('worksheets.builder.errors.name'));
      return;
    }
    if (draft.sections.some((s) => videoProblem(s.video))) {
      setShowVideoErrors(true);
      setProblem(t('worksheets.builder.video.unsupported'));
      return;
    }
    if (draft.sections.some((s) => s.fields.some((f) => !f.label.trim()))) {
      setProblem(t('worksheets.builder.errors.label'));
      return;
    }
    if (
      draft.sections.some((s) =>
        s.fields.some(
          (f) => CHOICE_TYPES.includes(f.type) && !f.options.some((o) => o.trim()),
        ),
      )
    ) {
      setProblem(t('worksheets.builder.errors.options'));
      return;
    }
    if (
      draft.sections.some((s) =>
        s.fields.some(
          (f) => f.type === 'scale' && (f.min === null || f.max === null || f.min >= f.max),
        ),
      )
    ) {
      setProblem(t('worksheets.builder.errors.scale'));
      return;
    }
    if (mode === 'publish' && !templateId && !LIBRARY_TEMPLATE_ID.test(libraryId.trim())) {
      setProblem(t('backoffice.worksheets.templateIdHelp'));
      return;
    }
    const content = contentOfDraft(draft);
    const input: Record<string, unknown> =
      mode === 'define'
        ? {
            ...content,
            ...(rightsConfirmed ? { rightsConfirmed: true } : {}),
            copiedFrom: draft.copiedFrom,
            ...(importedFrom ? { importedFrom } : {}),
          }
        : mode === 'revise'
        ? { ...content, templateId }
        : { ...content, templateId: templateId ?? libraryId.trim() };
    try {
      // the both-ends codec (shape only — the API runs it again); the rules are the server's
      if (mode === 'define') await defineWorksheetTemplateInputSchema.validate(input);
    } catch {
      setProblem(t('worksheets.builder.errors.incomplete'));
      return;
    }
    setSaving(true);
    const r = await run(OPERATION[mode], input, t('worksheets.builder.failed'));
    setSaving(false);
    if (!r.ok) {
      if (r.code && SETTINGS_CODES.includes(r.code)) setSettingsProblem(r.message);
      else setProblem(r.message);
      return;
    }
    message.success(t(mode === 'publish' ? 'backoffice.worksheets.published' : 'worksheets.builder.saved'));
    setWarnWhen(false);
    onSaved({ templateId: r.templateId, version: r.version });
  };

  const fieldTypeOptions = FIELD_TYPES.map((x) => ({
    value: x,
    label: t(`worksheets.fieldType.${x}`),
  }));

  const fieldCard = (s: DraftSection, f: DraftField, handle: ReactNode) => {
    const active = activeField === f.fieldId;
    return (
      <Card
        size="small"
        onClick={() => setActiveField(f.fieldId)}
        style={{
          marginBottom: 8,
          borderColor: active ? '#8aa899' : undefined,
          cursor: active ? 'default' : 'pointer',
        }}
        data-testid={`worksheet-field-${f.fieldId}`}
      >
        <Space align="start" style={{ width: '100%' }}>
          {handle}
          <div style={{ flex: 1, minWidth: 0 }}>
            {!active ? (
              <Typography.Text data-testid={`worksheet-field-summary-${f.fieldId}`}>
                {f.label || t('worksheets.builder.card.untitled')}
                {f.required ? ' *' : ''}
                <Typography.Text type="secondary"> · {t(`worksheets.fieldType.${f.type}`)}</Typography.Text>
              </Typography.Text>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size={6}>
                <Input
                  value={f.label}
                  autoFocus
                  maxLength={1000}
                  placeholder={t('worksheets.builder.card.label')}
                  onChange={(e) => patchField(s.sectionId, f.fieldId, { label: e.target.value })}
                  data-testid={`worksheet-field-label-${f.fieldId}`}
                />
                <Space wrap>
                  <Select
                    style={{ minWidth: 180 }}
                    value={f.type}
                    // eight types — render them all (antd's virtual list keeps only ~7 in the DOM)
                    virtual={false}
                    options={fieldTypeOptions}
                    onChange={(type: FieldType) =>
                      patchField(s.sectionId, f.fieldId, {
                        type,
                        options: CHOICE_TYPES.includes(type)
                          ? f.options.length
                            ? f.options
                            : ['', '']
                          : [],
                        min: NUMERIC_TYPES.includes(type) ? f.min ?? (type === 'scale' ? 0 : null) : null,
                        max: NUMERIC_TYPES.includes(type) ? f.max ?? (type === 'scale' ? 10 : null) : null,
                      })
                    }
                    data-testid={`worksheet-field-type-${f.fieldId}`}
                  />
                  <Space size={4}>
                    <Switch
                      size="small"
                      checked={f.required}
                      onChange={(required) => patchField(s.sectionId, f.fieldId, { required })}
                      data-testid={`worksheet-field-required-${f.fieldId}`}
                    />
                    <Typography.Text>{t('worksheets.builder.card.required')}</Typography.Text>
                  </Space>
                </Space>
                {CHOICE_TYPES.includes(f.type) ? (
                  <Space direction="vertical" style={{ width: '100%' }} size={4}>
                    {f.options.map((o, i) => (
                      <Space key={i} style={{ width: '100%' }}>
                        <Input
                          value={o}
                          maxLength={200}
                          placeholder={t('worksheets.builder.card.option', { n: i + 1 })}
                          onChange={(e) =>
                            patchField(s.sectionId, f.fieldId, {
                              options: f.options.map((x, j) => (j === i ? e.target.value : x)),
                            })
                          }
                          data-testid={`worksheet-field-option-${f.fieldId}-${i}`}
                        />
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          aria-label={t('worksheets.builder.card.removeOption')}
                          onClick={() =>
                            patchField(s.sectionId, f.fieldId, {
                              options: f.options.filter((_, j) => j !== i),
                            })
                          }
                        />
                      </Space>
                    ))}
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        patchField(s.sectionId, f.fieldId, { options: [...f.options, ''] })
                      }
                      data-testid={`worksheet-field-add-option-${f.fieldId}`}
                    >
                      {t('worksheets.builder.card.addOption')}
                    </Button>
                  </Space>
                ) : null}
                {NUMERIC_TYPES.includes(f.type) ? (
                  <Space>
                    <Typography.Text>{t('worksheets.builder.card.min')}</Typography.Text>
                    <InputNumber
                      value={f.min}
                      onChange={(v) => patchField(s.sectionId, f.fieldId, { min: v ?? null })}
                      data-testid={`worksheet-field-min-${f.fieldId}`}
                    />
                    <Typography.Text>{t('worksheets.builder.card.max')}</Typography.Text>
                    <InputNumber
                      value={f.max}
                      onChange={(v) => patchField(s.sectionId, f.fieldId, { max: v ?? null })}
                      data-testid={`worksheet-field-max-${f.fieldId}`}
                    />
                  </Space>
                ) : null}
              </Space>
            )}
          </div>
          <Space size={0}>
            <Tooltip title={t('worksheets.builder.card.duplicate')}>
              <Button
                type="text"
                icon={<CopyOutlined />}
                aria-label={t('worksheets.builder.card.duplicate')}
                onClick={(e) => {
                  e.stopPropagation();
                  duplicateField(s.sectionId, f.fieldId);
                }}
                data-testid={`worksheet-field-duplicate-${f.fieldId}`}
              />
            </Tooltip>
            <Tooltip title={t('worksheets.builder.card.delete')}>
              <Button
                type="text"
                icon={<DeleteOutlined />}
                aria-label={t('worksheets.builder.card.delete')}
                onClick={(e) => {
                  e.stopPropagation();
                  deleteField(s.sectionId, f.fieldId);
                }}
                data-testid={`worksheet-field-delete-${f.fieldId}`}
              />
            </Tooltip>
          </Space>
        </Space>
      </Card>
    );
  };

  const videoCard = (s: DraftSection) => {
    const v = s.video;
    if (!v) {
      return (
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() =>
            patchSection(s.sectionId, {
              video: { source: 'learn', ref: '', title: '' },
            })
          }
          data-testid={`worksheet-section-add-video-${s.sectionId}`}
        >
          {t('worksheets.builder.addVideo')}
        </Button>
      );
    }
    const learn = v.source === 'learn';
    const unsupported = showVideoErrors && videoProblem(v);
    const catalogue = learnVideoOptionsOf(draft.language, v.ref);
    return (
      <Card
        size="small"
        title={t('worksheets.builder.video.title')}
        data-testid={`worksheet-video-${s.sectionId}`}
        extra={
          <Button
            size="small"
            type="text"
            danger
            onClick={() => patchSection(s.sectionId, { video: null })}
            data-testid={`worksheet-video-remove-${s.sectionId}`}
          >
            {t('worksheets.builder.video.remove')}
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size={6}>
          <Radio.Group
            value={learn ? 'learn' : 'link'}
            onChange={(e) =>
              patchSection(s.sectionId, {
                video:
                  e.target.value === 'learn'
                    ? { source: 'learn', ref: '', title: '' }
                    : { source: 'youtube', ref: '', title: v.title },
              })
            }
            data-testid={`worksheet-video-source-${s.sectionId}`}
          >
            <Radio value="learn" data-testid={`worksheet-video-source-learn-${s.sectionId}`}>
              {t('worksheets.builder.video.learn')}
            </Radio>
            <Radio value="link" data-testid={`worksheet-video-source-link-${s.sectionId}`}>
              {t('worksheets.builder.video.link')}
            </Radio>
          </Radio.Group>
          {learn ? (
            <Select
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
              value={v.ref || undefined}
              placeholder={t('worksheets.builder.video.learnPlaceholder')}
              options={catalogue.map((c) => ({ value: c.id, label: c.name }))}
              onChange={(ref: string) =>
                patchSection(s.sectionId, {
                  video: {
                    source: 'learn',
                    ref,
                    title: v.title || catalogue.find((c) => c.id === ref)?.name || '',
                  },
                })
              }
              data-testid={`worksheet-video-learn-${s.sectionId}`}
            />
          ) : (
            <>
              <Input
                value={v.ref}
                placeholder={t('worksheets.builder.video.linkPlaceholder')}
                onChange={(e) => {
                  const ref = e.target.value;
                  patchSection(s.sectionId, {
                    video: { ...v, source: linkSourceOf(ref) ?? v.source, ref },
                  });
                }}
                status={unsupported ? 'error' : undefined}
                data-testid={`worksheet-video-link-${s.sectionId}`}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('worksheets.builder.video.linkHelp')}
              </Typography.Text>
            </>
          )}
          {unsupported ? (
            <Typography.Text type="danger" data-testid={`worksheet-video-unsupported-${s.sectionId}`}>
              {t('worksheets.builder.video.unsupported')}
            </Typography.Text>
          ) : null}
          <Input
            value={v.title}
            maxLength={200}
            placeholder={t('worksheets.builder.video.titlePlaceholder')}
            onChange={(e) => patchSection(s.sectionId, { video: { ...v, title: e.target.value } })}
            data-testid={`worksheet-video-title-${s.sectionId}`}
          />
        </Space>
      </Card>
    );
  };

  const sectionCard = (s: DraftSection, index: number, handle: ReactNode) => (
    <Card
      style={{ marginBottom: 16 }}
      title={
        <Space>
          {handle}
          <span>{t('worksheets.builder.sectionN', { n: index + 1 })}</span>
        </Space>
      }
      extra={
        draft.sections.length > 1 ? (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteSection(s.sectionId)}
            data-testid={`worksheet-section-delete-${s.sectionId}`}
          >
            {t('worksheets.builder.deleteSection')}
          </Button>
        ) : null
      }
      data-testid={`worksheet-section-${s.sectionId}`}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Input
          value={s.title}
          maxLength={200}
          placeholder={t('worksheets.builder.sectionTitle')}
          onChange={(e) => patchSection(s.sectionId, { title: e.target.value })}
          data-testid={`worksheet-section-title-${s.sectionId}`}
        />
        <Input.TextArea
          value={s.text}
          autoSize={{ minRows: 2, maxRows: 10 }}
          maxLength={20000}
          placeholder={t('worksheets.builder.sectionText')}
          onChange={(e) => patchSection(s.sectionId, { text: e.target.value })}
          data-testid={`worksheet-section-text-${s.sectionId}`}
        />
        {videoCard(s)}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onFieldsDragEnd(s.sectionId)}
          accessibility={accessibility}
        >
          <SortableContext
            items={s.fields.map((f) => f.fieldId)}
            strategy={verticalListSortingStrategy}
          >
            {s.fields.map((f) => (
              <Sortable key={f.fieldId} id={f.fieldId}>
                {(h) => fieldCard(s, f, h)}
              </Sortable>
            ))}
          </SortableContext>
        </DndContext>
        <Button
          icon={<PlusOutlined />}
          onClick={() => addField(s.sectionId)}
          data-testid={`worksheet-add-question-${s.sectionId}`}
        >
          {t('worksheets.builder.addQuestion')}
        </Button>
      </Space>
    </Card>
  );

  const numeric = numericFields(draft);
  const settings = (
    <Card
      title={t('worksheets.builder.settings.title')}
      size="small"
      data-testid="worksheet-settings"
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div>
          <Typography.Text>{t('worksheets.builder.settings.category')}</Typography.Text>
          <Select
            style={{ width: '100%' }}
            value={draft.category}
            options={categoryChoices(draft.category).map((c) => ({
              value: c,
              label: t(`worksheets.category.${c}`),
            }))}
            onChange={(category) => patch({ category })}
            data-testid="worksheet-settings-category"
          />
        </div>
        <div>
          <Typography.Text>{t('worksheets.builder.settings.language')}</Typography.Text>
          <Select
            style={{ width: '100%' }}
            value={draft.language}
            options={LANGUAGES.map((l) => ({ value: l, label: t(`worksheets.language.${l}`) }))}
            onChange={(language) => patch({ language })}
            data-testid="worksheet-settings-language"
          />
        </div>
        <Space>
          <Switch
            checked={draft.signatureRequired}
            onChange={(signatureRequired) => patch({ signatureRequired })}
            data-testid="worksheet-settings-signature"
          />
          <Typography.Text>{t('worksheets.builder.settings.signature')}</Typography.Text>
        </Space>
        <Space>
          <Switch
            checked={draft.repeatable}
            onChange={(repeatable) => patch({ repeatable })}
            data-testid="worksheet-settings-repeatable"
          />
          <Typography.Text>{t('worksheets.builder.settings.repeatable')}</Typography.Text>
        </Space>
        <Space>
          <Switch
            checked={draft.scoring.enabled}
            onChange={(enabled) =>
              patch({
                scoring: {
                  enabled,
                  fields: draft.scoring.fields,
                  bands: draft.scoring.bands.length
                    ? draft.scoring.bands
                    : [{ min: 0, max: 10, label: '' }],
                },
              })
            }
            data-testid="worksheet-settings-scoring"
          />
          <Typography.Text>{t('worksheets.builder.settings.scoring')}</Typography.Text>
        </Space>
        {draft.scoring.enabled ? (
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('worksheets.builder.settings.scoringHelp')}
            </Typography.Text>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              value={draft.scoring.fields}
              placeholder={t('worksheets.builder.settings.scoringFields')}
              options={numeric.map((f) => ({ value: f.fieldId, label: f.label || f.fieldId }))}
              onChange={(fields: string[]) => patch({ scoring: { ...draft.scoring, fields } })}
              data-testid="worksheet-settings-scoring-fields"
            />
            {draft.scoring.bands.map((b, i) => (
              <Space key={i} data-testid={`worksheet-settings-band-${i}`}>
                <InputNumber
                  value={b.min}
                  onChange={(min) =>
                    patch({
                      scoring: {
                        ...draft.scoring,
                        bands: draft.scoring.bands.map((x, j) => (j === i ? { ...x, min: min ?? 0 } : x)),
                      },
                    })
                  }
                  data-testid={`worksheet-settings-band-${i}-min`}
                />
                <InputNumber
                  value={b.max}
                  onChange={(max) =>
                    patch({
                      scoring: {
                        ...draft.scoring,
                        bands: draft.scoring.bands.map((x, j) => (j === i ? { ...x, max: max ?? 0 } : x)),
                      },
                    })
                  }
                  data-testid={`worksheet-settings-band-${i}-max`}
                />
                <Input
                  value={b.label}
                  maxLength={100}
                  placeholder={t('worksheets.builder.settings.bandLabel')}
                  onChange={(e) =>
                    patch({
                      scoring: {
                        ...draft.scoring,
                        bands: draft.scoring.bands.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                      },
                    })
                  }
                  data-testid={`worksheet-settings-band-${i}-label`}
                />
              </Space>
            ))}
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                patch({
                  scoring: {
                    ...draft.scoring,
                    bands: [...draft.scoring.bands, { min: 0, max: 0, label: '' }],
                  },
                })
              }
              data-testid="worksheet-settings-add-band"
            >
              {t('worksheets.builder.settings.addBand')}
            </Button>
          </Space>
        ) : null}
        {settingsProblem ? (
          <Alert type="error" showIcon message={settingsProblem} data-testid="worksheet-settings-error" />
        ) : null}
      </Space>
    </Card>
  );

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="worksheet-builder">
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        {heading}
        <Space>
          {headerExtra}
          <Button
            onClick={() => {
              setWarnWhen(false);
              onCancel();
            }}
            data-testid="worksheet-builder-cancel"
          >
            {cancelLabel ?? t('worksheets.builder.cancel')}
          </Button>
          <Button type="primary" loading={saving}
            onClick={save}
            data-testid="worksheet-builder-save"
          >
            {t(
              mode === 'publish'
                ? 'backoffice.worksheets.publish'
                : mode === 'revise'
                ? 'worksheets.builder.revise'
                : 'worksheets.builder.save',
            )}
          </Button>
        </Space>
      </Space>
      {mode === 'revise' ? (
        <Typography.Text type="secondary" data-testid="worksheet-builder-revise-note">
          {t('worksheets.builder.reviseNote')}
        </Typography.Text>
      ) : null}
      {notice}
      {problem ? (
        <Alert type="error" showIcon message={problem} data-testid="worksheet-builder-error" />
      ) : null}
      <Row gutter={24}>
        <Col xs={24} lg={15}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {mode === 'publish' && !templateId ? (
              <div>
                <Typography.Text>{t('backoffice.worksheets.templateId')}</Typography.Text>
                <Input
                  value={libraryId}
                  placeholder="lib-wt-…"
                  onChange={(e) => setLibraryId(e.target.value)}
                  data-testid="worksheet-builder-template-id"
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('backoffice.worksheets.templateIdHelp')}
                </Typography.Text>
              </div>
            ) : null}
            <Input
              size="large"
              value={draft.name}
              maxLength={200}
              placeholder={t('worksheets.builder.name')}
              onChange={(e) => patch({ name: e.target.value })}
              data-testid="worksheet-builder-name"
            />
            {draft.copiedFrom && mode === 'define' ? (
              <Typography.Text type="secondary" data-testid="worksheet-builder-copied-from">
                {t('worksheets.builder.copiedFrom')}
              </Typography.Text>
            ) : null}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onSectionsDragEnd}
              accessibility={accessibility}
            >
              <SortableContext
                items={draft.sections.map((s) => s.sectionId)}
                strategy={verticalListSortingStrategy}
              >
                {draft.sections.map((s, i) => (
                  <Sortable key={s.sectionId} id={s.sectionId}>
                    {(h) => sectionCard(s, i, h)}
                  </Sortable>
                ))}
              </SortableContext>
            </DndContext>
            <Button icon={<PlusOutlined />} onClick={addSection} data-testid="worksheet-add-section">
              {t('worksheets.builder.addSection')}
            </Button>
          </Space>
        </Col>
        <Col xs={24} lg={9}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {settings}
            <Card title={t('worksheets.builder.preview')} size="small">
              <WorksheetPhonePreview draft={draft} />
            </Card>
          </Space>
        </Col>
      </Row>
    </Space>
  );
};
